import { containers } from "@builder/store/slices/BuilderSlice/builderMap";
import { getCurrentLayer, getCurrentRoot } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { moveNode } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { BaseLayer, BuildNode } from "@common/build/types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tree } from "react-arborist";
import SingleTreeNode, { NodeHeight, TreeNode } from "./SingleTreeNode";

import "./style.scss";
import useSelectedNodeScroll from "./useSelectedNodeScroll";


const BuildTree: React.FC<{ isResizing: boolean }> = ({ isResizing }) => {
  const dispatch = useAppDispatch();
  const build = useAppSelector(getCurrentLayer);
  const root = useAppSelector(getCurrentRoot);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const treeRef = useRef<any>(null);
  const [treeHeight, setTreeHeight] = useState<number>(500);

  const treeData = useMemo(() => transformToTree(build), [build]);

  useEffect(() => {
    if (wrapperRef.current) setTreeHeight(wrapperRef.current.clientHeight);
  }, [isResizing]);

  // Use the simplified hook - no callback needed
  useSelectedNodeScroll(treeRef);

  function transformToTree(currentLayer: BaseLayer): TreeNode[] | undefined {
    if (!currentLayer) return [];
    return [buildTreeRecursive(currentLayer.root, currentLayer.components)];
  }

  function buildTreeRecursive(nodeId: string, components: { [key: string]: BuildNode }): TreeNode {
    const node = components[nodeId];
    const allChildren =
      node.children?.map(childId => {
        const child = components[childId];
        return {
          id: childId,
          name: child.name,
          customName: child.customName
        };
      }) || [];
    let filteredChildren: string[] = [];

    if (node.children && node.children.length > 0) {
      if (node.childrenFilterKey) {
        if (typeof node.previewChildIndex !== "number") {
          filteredChildren = [node.children[0]];
        } else if (node.previewChildIndex >= 0 && node.previewChildIndex < node.children.length) {
          filteredChildren = [node.children[node.previewChildIndex]];
        }
      } else filteredChildren = node.children;
    }

    const childTreeNodes: TreeNode[] = filteredChildren.map(childId => buildTreeRecursive(childId, components));

    return {
      id: nodeId,
      name: node.name,
      customName: node.customName,
      allChildren,
      noDelete: node.noDelete,
      noCopy: node.noCopy,
      children: containers.includes(node.name) ? childTreeNodes : undefined,
      childrenFilterKey: node.childrenFilterKey,
      previewChildIndex: node.previewChildIndex,
      order: 0
    };
  }

  const onMove = (args: { dragIds: string[]; parentId: string | null; index: number }) => {
    const nodeId = args.dragIds[0];
    const targetId = args.parentId || "";
    let index = args.index;

    if (build.components && build.components[nodeId]) {
      const node = build.components[nodeId];
      if (node.parent === targetId) {
        const parentNode = build.components[targetId];
        if (parentNode && parentNode.children) {
          const currentIndex = parentNode.children.indexOf(nodeId);
          if (currentIndex !== -1 && index > currentIndex) {
            index -= 1;
          }
        }
      }
    }

    dispatch(moveNode({ nodeId, targetId, index }));
  };

  return (
    <div className="build-tree">
      <div className="build-tree-wrapper" ref={wrapperRef}>
        <Tree
          data={treeData}
          onMove={onMove}
          rowHeight={NodeHeight}
          height={treeHeight}
          width="100%"
          paddingTop={8}
          indent={15}
          ref={treeRef}
        >
          {({ node, dragHandle, ...rest }) => (
            <SingleTreeNode node={node} dragHandle={dragHandle} rest={rest} root={root} />
          )}
        </Tree>
      </div>
    </div>
  );
};

export default React.memo(BuildTree);